import { z } from "zod";
import { billKindSchema, paymentMethodSchema } from "./enums";

/**
 * Owner reports (MG-07). See docs/architecture/ordering-billing-gst.md,
 * Reports, and api.md, Reports (staff). Every report is over business dates,
 * `from` to `to` inclusive, at most 366 days. Sales are final bills only:
 * void bills are left out and listed by `staff.reports.voids`. Money is
 * integer paise.
 */

/** The longest range one report may cover, in days, both ends included. */
export const REPORT_MAX_DAYS = 366;

const businessDateSchema = z.iso.date();
const paise = z.number().int().nonnegative();
const signedPaise = z.number().int();
const count = z.number().int().nonnegative();

/** Days from `from` to `to`, both included: 2026-09-01 to 2026-09-30 is 30. */
export function reportDays(from: string, to: string): number {
	return (
		Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
	);
}

/** `{ from, to }` business dates (YYYY-MM-DD), `from` not after `to`, at most 366 days. */
export const reportRangeInputSchema = z
	.object({ from: businessDateSchema, to: businessDateSchema })
	.refine((r) => r.from <= r.to, {
		message: "The start must not be after the end",
		path: ["to"],
	})
	.refine((r) => r.from > r.to || reportDays(r.from, r.to) <= REPORT_MAX_DAYS, {
		message: `At most ${REPORT_MAX_DAYS} days`,
		path: ["to"],
	});
export type ReportRangeInput = z.input<typeof reportRangeInputSchema>;

const range = { from: businessDateSchema, to: businessDateSchema };

/**
 * Amounts of a set of final bills, the columns of `bills` summed. These
 * always hold: total = taxable + CGST + SGST + round-off.
 */
export const billTotalsSchema = z.object({
	bills: count,
	subtotalPaise: paise,
	discountPaise: paise,
	serviceChargePaise: paise,
	taxablePaise: paise,
	cgstPaise: paise,
	sgstPaise: paise,
	roundOffPaise: signedPaise,
	totalPaise: paise,
});
export type BillTotals = z.infer<typeof billTotalsSchema>;

export const paymentMethodRowSchema = z.object({
	method: paymentMethodSchema,
	/** Confirmed payments; reversed ones do not count. */
	payments: count,
	amountPaise: paise,
});
export type PaymentMethodRow = z.infer<typeof paymentMethodRowSchema>;

/**
 * `staff.reports.summary`: sales, bills, average bill, discounts, taxes,
 * voids, by payment method, and the sales of each business date for the bar chart.
 */
export const reportSummarySchema = z.object({
	...range,
	sales: billTotalsSchema.extend({
		/** total ÷ bills, rounded half up; 0 without bills. */
		averageBillPaise: paise,
		/** CGST + SGST. */
		taxPaise: paise,
	}),
	payments: z.array(paymentMethodRowSchema),
	/** Confirmed payments against these bills. */
	receivedPaise: paise,
	/** Sales not yet paid: total − received. */
	outstandingPaise: signedPaise,
	voids: z.object({ bills: count, totalPaise: paise }),
	/** Only business dates with bills, oldest first. */
	days: z.array(z.object({ businessDate: businessDateSchema, bills: count, totalPaise: paise })),
});
export type ReportSummary = z.infer<typeof reportSummarySchema>;

/** Quantity and value of what was sold, from the bills' lines. */
const lineValues = {
	quantity: count,
	/** Price × quantity, before discounts. */
	grossPaise: paise,
	/** Item discounts and each line's share of the bill discount. */
	discountPaise: paise,
	/** gross − discount. Before service charge; with tax-inclusive prices, tax included. */
	netPaise: paise,
};
export const lineTotalsSchema = z.object(lineValues);
export type LineTotals = z.infer<typeof lineTotalsSchema>;

/** `staff.reports.byItem`: one row per menu item, by net value, highest first. */
export const reportByItemSchema = z.object({
	...range,
	rows: z.array(
		z.object({
			/** Null for a line whose menu item is gone; such lines are grouped by name. */
			itemId: z.uuid().nullable(),
			/** The name on the most recent bill. */
			name: z.string(),
			categoryId: z.uuid().nullable(),
			categoryName: z.string().nullable(),
			...lineValues,
		}),
	),
	totals: lineTotalsSchema,
});
export type ReportByItem = z.infer<typeof reportByItemSchema>;

/** `staff.reports.byCategory`: one row per menu category, by net value, highest first. */
export const reportByCategorySchema = z.object({
	...range,
	rows: z.array(
		z.object({
			/** Null for lines whose menu item is gone. */
			categoryId: z.uuid().nullable(),
			name: z.string().nullable(),
			...lineValues,
		}),
	),
	totals: lineTotalsSchema,
});
export type ReportByCategory = z.infer<typeof reportByCategorySchema>;

/** `staff.reports.byPayment`: confirmed payments against the range's final bills, by method. */
export const reportByPaymentSchema = z.object({
	...range,
	rows: z.array(paymentMethodRowSchema),
	salesPaise: paise,
	receivedPaise: paise,
	outstandingPaise: signedPaise,
});
export type ReportByPayment = z.infer<typeof reportByPaymentSchema>;

/** `staff.reports.byStaff`: bills and value per person who finalised them, highest first. */
export const reportByStaffSchema = z.object({
	...range,
	rows: z.array(
		z.object({
			/** Null when the account was deleted. */
			userId: z.uuid().nullable(),
			name: z.string().nullable(),
			bills: count,
			totalPaise: paise,
			discountPaise: paise,
		}),
	),
});
export type ReportByStaff = z.infer<typeof reportByStaffSchema>;

/** `staff.reports.byHour`: bills per clock hour (0 to 23) in the restaurant's time zone, all 24. */
export const reportByHourSchema = z.object({
	...range,
	rows: z.array(
		z.object({ hour: z.number().int().min(0).max(23), bills: count, totalPaise: paise }),
	),
});
export type ReportByHour = z.infer<typeof reportByHourSchema>;

/**
 * `staff.reports.taxSummary`: for the accountant, taxable value and tax per
 * rate per month (of the business date). Tax invoices give a row per rate;
 * bills of supply and plain bills carry no tax and give one row each, with
 * `rateBp` null.
 */
export const reportTaxSummarySchema = z.object({
	...range,
	rows: z.array(
		z.object({
			/** YYYY-MM */
			month: z.string().regex(/^\d{4}-\d{2}$/),
			kind: billKindSchema,
			rateBp: z.number().int().min(0).max(10000).nullable(),
			taxablePaise: paise,
			cgstPaise: paise,
			sgstPaise: paise,
		}),
	),
	months: z.array(billTotalsSchema.extend({ month: z.string().regex(/^\d{4}-\d{2}$/) })),
	totals: billTotalsSchema,
});
export type ReportTaxSummary = z.infer<typeof reportTaxSummarySchema>;

/** The most void bills `staff.reports.voids` lists; `count` and the total cover all. */
export const REPORT_VOIDS_LIMIT = 500;

/** `staff.reports.voids`: void bills of the range, newest first, kept out of every other report. */
export const reportVoidsSchema = z.object({
	...range,
	count,
	totalPaise: paise,
	bills: z.array(
		z.object({
			id: z.uuid(),
			billNo: z.string(),
			businessDate: businessDateSchema,
			finalizedAt: z.iso.datetime(),
			totalPaise: paise,
			voidReason: z.string(),
			voidedAt: z.iso.datetime(),
			voidedBy: z.object({ id: z.uuid(), name: z.string().nullable() }).nullable(),
		}),
	),
	/** True when more than `REPORT_VOIDS_LIMIT` bills were void; the CSV has them all. */
	truncated: z.boolean(),
});
export type ReportVoids = z.infer<typeof reportVoidsSchema>;

/** What `staff.reports.exportCsv` can export: each report, and every bill for the accountant. */
export const CSV_REPORTS = [
	"summary",
	"byItem",
	"byCategory",
	"byPayment",
	"byStaff",
	"byHour",
	"taxSummary",
	"voids",
	"bills",
] as const;
export const csvReportSchema = z.enum(CSV_REPORTS);
export type CsvReport = z.infer<typeof csvReportSchema>;

export const exportCsvInputSchema = z
	.object({ report: csvReportSchema, from: businessDateSchema, to: businessDateSchema })
	.refine((r) => r.from <= r.to, {
		message: "The start must not be after the end",
		path: ["to"],
	})
	.refine((r) => r.from > r.to || reportDays(r.from, r.to) <= REPORT_MAX_DAYS, {
		message: `At most ${REPORT_MAX_DAYS} days`,
		path: ["to"],
	});
export type ExportCsvInput = z.input<typeof exportCsvInputSchema>;

/** A CSV file: UTF-8 with a byte order mark, CRLF lines, amounts in rupees with two decimals. */
export const csvFileSchema = z.object({
	/** Such as `luckymenu-spice-garden-items-2026-09-01-to-2026-09-30.csv`. */
	fileName: z.string(),
	contentType: z.literal("text/csv; charset=utf-8"),
	csv: z.string(),
	rows: count,
});
export type CsvFile = z.infer<typeof csvFileSchema>;

/**
 * Paise as plain rupees with two decimals, for files: 54000 → "540.00",
 * -30 → "-0.30". Integer arithmetic only.
 */
export function paiseToRupees(paise: number): string {
	if (!Number.isSafeInteger(paise)) throw new RangeError(`Not an integer paise value: ${paise}`);
	const abs = Math.abs(paise);
	const text = `${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
	return paise < 0 ? `-${text}` : text;
}

/**
 * Day close (MG-08, P11-T07): one business date's totals by payment method,
 * bills, voids, discounts and cancelled items. Stored in `day_closings.totals`
 * when the day is closed; printable on the thermal printer.
 */
export const dayCloseTotalsSchema = z.object({
	businessDate: businessDateSchema,
	/** The day's final bills, their columns summed. */
	sales: billTotalsSchema,
	payments: z.array(paymentMethodRowSchema),
	receivedPaise: paise,
	/** Sales not yet paid. */
	outstandingPaise: signedPaise,
	voids: z.object({ bills: count, totalPaise: paise }),
	/** Final bills with a discount, and the discounts' sum. */
	discounts: z.object({ bills: count, totalPaise: paise }),
	/** Items cancelled from the day's orders after they were sent: units, and their value. */
	cancelledItems: z.object({ quantity: count, valuePaise: paise }),
});
export type DayCloseTotals = z.infer<typeof dayCloseTotalsSchema>;

export const dayClosingSchema = z.object({
	id: z.uuid(),
	closedAt: z.iso.datetime(),
	closedBy: z.object({ id: z.uuid(), name: z.string().nullable() }).nullable(),
	/** The totals when it was closed. */
	totals: dayCloseTotalsSchema,
});
export type DayClosing = z.infer<typeof dayClosingSchema>;

/**
 * `staff.dayClose.get`: the day's totals now, and its closing if it was
 * closed (whose totals may differ when bills changed afterwards).
 */
export const dayCloseSchema = z.object({
	businessDate: businessDateSchema,
	totals: dayCloseTotalsSchema,
	closing: dayClosingSchema.nullable(),
});
export type DayClose = z.infer<typeof dayCloseSchema>;

export const dayCloseInputSchema = z.object({ businessDate: businessDateSchema });

/** `staff.dayClose.print`: the closing on the bill printer, or the one named. `id` is the job's, made by the client. */
export const printDayCloseInputSchema = z.object({
	id: z.uuid(),
	businessDate: businessDateSchema,
	printerId: z.uuid().optional(),
});
